import React from 'react';
import { Skeleton } from '../ui/skeleton';
import { Card, CardContent, CardHeader } from '../ui/card';

/**
 * Skeleton for the HoursBarChart - shows animated horizontal bars
 */
export const HoursBarChartSkeleton: React.FC = () => (
  <Card>
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-32" />
    </CardHeader>
    <CardContent className="space-y-3">
      {[80, 60, 45, 30, 20].map((width, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-16 flex-shrink-0" />
          <Skeleton className="h-6 rounded-full" style={{ width: `${width}%` }} />
          <Skeleton className="h-4 w-10 flex-shrink-0" />
        </div>
      ))}
    </CardContent>
  </Card>
);

/**
 * Skeleton for the ApprovalDonutChart - shows animated circle
 */
export const ApprovalDonutChartSkeleton: React.FC = () => (
  <Card>
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-36" />
    </CardHeader>
    <CardContent className="flex items-center justify-center py-4">
      <div className="relative">
        <Skeleton className="h-40 w-40 rounded-full" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-24 w-24 rounded-full bg-background" />
        </div>
      </div>
      <div className="ml-6 space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

/**
 * Skeleton for the MonthlyTotalsTable - shows animated table rows
 */
export const MonthlyTotalsTableSkeleton: React.FC = () => (
  <Card>
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-32" />
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        {/* Header row */}
        <div className="flex gap-4 pb-2 border-b">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-4 w-16" />
        </div>
        {/* Data rows */}
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-4 py-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-10" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

/**
 * Skeleton for the TeamMemberChart - shows animated horizontal bars for team members
 */
export const TeamMemberChartSkeleton: React.FC = () => (
  <Card>
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-40" />
    </CardHeader>
    <CardContent className="space-y-4">
      {[75, 65, 55, 45].map((width, i) => (
        <div key={i} className="space-y-1">
          <div className="flex justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-12" />
          </div>
          <Skeleton className="h-3 rounded-full" style={{ width: `${width}%` }} />
        </div>
      ))}
    </CardContent>
  </Card>
);

/**
 * Skeleton for stat cards - 4 cards in a grid
 */
export const StatCardsSkeleton: React.FC = () => (
  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
    {[1, 2, 3, 4].map((i) => (
      <Card key={i}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4 rounded" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-3 w-16" />
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Full dashboard skeleton combining all chart skeletons
 */
export const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="space-y-2">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-48" />
    </div>

    {/* Timeframe selector */}
    <div className="flex gap-2">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-9 w-24 rounded-md" />
      ))}
    </div>

    {/* Stat cards */}
    <StatCardsSkeleton />

    {/* Charts grid */}
    <div className="grid gap-6 md:grid-cols-2">
      <HoursBarChartSkeleton />
      <ApprovalDonutChartSkeleton />
    </div>

    {/* Monthly totals */}
    <MonthlyTotalsTableSkeleton />
  </div>
);
